import { z } from "zod";
export const ReloadStatus = z.enum([
    "QUEUED",
    "RELOADING",
    "SUCCEEDED",
    "FAILED",
    "CANCELLED",
]);
export const ReloadType = z.enum(["IMMEDIATE", "SCHEDULED"]);
export const ImmediateReloadBody = z.object({
    appId: z.string().min(1),
    connectionId: z.number().int().positive(),
    partial: z.boolean().optional().default(false),
});
export const ActiveReloadView = z.object({
    reloadId: z.string(),
    qlikReloadId: z.string(),
    connectionId: z.number().int(),
    appId: z.string(),
    appName: z.string(),
    status: ReloadStatus,
    startTime: z.string().datetime(),
    progressPercentage: z.number().int().min(0).max(100),
    lastHeartbeat: z.string().datetime(),
    reloadType: ReloadType,
    retryAttempt: z.number().int(),
});
export const ReloadHistoryView = z.object({
    reloadId: z.string(),
    scheduleId: z.number().int().nullable(),
    appId: z.string(),
    appName: z.string(),
    reloadType: ReloadType,
    status: ReloadStatus,
    triggeredBy: z.string().uuid().nullable(),
    triggeredByName: z.string().nullable(),
    startTime: z.string().datetime(),
    endTime: z.string().datetime().nullable(),
    durationSeconds: z.number().int().nullable(),
    errorMessage: z.string().nullable(),
    errorCode: z.string().nullable(),
    qlikReloadId: z.string().nullable(),
    qlikLogUrl: z.string().nullable(),
    partialReload: z.boolean(),
    retryAttempt: z.number().int(),
    // Bulk-operation tag. Non-null when this row came from POST /reloads/bulk
    // (every row from the same batch shares one value).
    operationId: z.string().nullable(),
});
export const ReloadHistoryQuery = z.object({
    appId: z.string().optional(),
    status: ReloadStatus.optional(),
    from: z.string().datetime().optional(),
    to: z.string().datetime().optional(),
    // Filter rows by bulk-operation id ("op_<nanoid>"). The History tab uses
    // this to drill into a single Bulk Reload batch.
    operationId: z.string().optional(),
    limit: z.coerce.number().int().min(1).max(200).default(50),
    offset: z.coerce.number().int().min(0).default(0),
});
// One row in the "external reloads" feed — apps/web fetches this for the
// Active + History tabs when the user toggles "Include external" on. Same
// shape Qlik returns, plus a derived `source` tag computed server-side by
// matching against our local reloads.
export const QlikReloadRow = z.object({
    qlikReloadId: z.string(),
    appId: z.string(),
    // Resolved app name from the Qlik items endpoint (cached per connection).
    // Null if the app has been deleted or the items lookup failed.
    appName: z.string().nullable(),
    connectionId: z.number().int(),
    connectionName: z.string(),
    status: ReloadStatus,
    // Qlik's `type` field — "external" (API), "manual" (Hub click),
    // "scheduled" (Qlik native scheduler), or others.
    type: z.string().nullable(),
    // Qlik's userId — opaque GUID; the UI just shows it as-is if present.
    userId: z.string().nullable(),
    startTime: z.string().datetime().nullable(),
    endTime: z.string().datetime().nullable(),
    duration: z.string().nullable(),
    log: z.string().nullable(),
    // Derived: source tag the UI renders.
    source: z.enum(["qlik-scheduler", "hub", "qlik-native", "external"]),
    // If we have a local match, surface our reloadId so the UI can deep-link.
    localReloadId: z.string().nullable(),
});
export const QlikReloadsResponse = z.object({
    data: z.array(QlikReloadRow),
});
//# sourceMappingURL=reload.js.map